import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { UserConfigurationState } from '@/types';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useUserConfigState, useUserConfigDispatch } from '@/context/UserConfigContext';
import Logger from '@/utils/logger';
import { ValidationContext } from '@/components/validation';
import { saveUserConfig } from '@/services/userConfigService';
import { sanitizeUserConfig } from '@/utils/sanitizeConfig';
import { toast } from 'sonner';

type WizardState = {
  currentStep: number;
  completedSteps: number[];
};

type WizardContextType = WizardState & {
  goToNextStep(): void;
  goToPreviousStep(): void;
  goToStep(step: number): void;
  isStepCompleted(step: number): boolean;
  markStepAsCompleted(step: number): void;
  submitOnboarding(): Promise<void>;
  isSubmitting: boolean; // Added isSubmitting
};

const OnboardingWizardContext = createContext<WizardContextType | undefined>(undefined);

export const OnboardingWizardProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const validationCtx = useContext(ValidationContext);
  const [searchParams] = useSearchParams();
  // step param is 1-indexed: ?step=1 = Crew Info, ?step=2 = Rate Sheet, etc.
  const devStep = import.meta.env.DEV
    ? Math.max(0, Math.min(5, (parseInt(searchParams.get('step') ?? '', 10) || 0) - 1))
    : 0;
  const devSeed = import.meta.env.DEV && searchParams.get('seed') === 'true';

  const [state, setState] = useState<WizardState>(() => {
    if (devStep > 0) {
      return { currentStep: devStep, completedSteps: Array.from({ length: devStep }, (_, i) => i) };
    }
    return { currentStep: 0, completedSteps: [] };
  });
  const queryClient = useQueryClient();

  const { mutateAsync: submitConfig, isPending: isSubmitting } = useMutation({
    mutationFn: (payload: UserConfigurationState) =>
      saveUserConfig(user!.id, payload),
    onSuccess: async () => {
      await refreshUserProfileStatus();
      queryClient.invalidateQueries({ queryKey: ['userConfig', user?.id] });
      toast.success('Onboarding completed successfully!');
      navigate('/dashboard');
    }
  });
  const userConfigState = useUserConfigState();
  const dispatch = useUserConfigDispatch();
  const { user, refreshUserProfileStatus } = useAuth();
  const navigate  = useNavigate();

  /* dev-only: seed prior step data when ?step=N&seed=true.
     Waits for user to be available so email override is reliable. */
  const seededRef = useRef(false);
  useEffect(() => {
    if (!devSeed || devStep < 1 || seededRef.current) return;
    if (!user?.email) return; // wait until auth resolves
    seededRef.current = true;
    import('@/utils/dev/onboardingSeedData').then(({ seedOnboardingData }) => {
      seedOnboardingData(devStep, dispatch, { email: user.email });
    });
  }, [devSeed, devStep, dispatch, user?.email]);

  /* navigation helpers */
  const goToNextStep       = () => setState(s => ({ ...s, currentStep: s.currentStep + 1 }));
  const goToPreviousStep   = () => setState(s => ({ ...s, currentStep: Math.max(0, s.currentStep - 1) }));
  const goToStep           = (step: number) => setState(s => ({ ...s, currentStep: step }));
  const isStepCompleted    = (step: number) => state.completedSteps.includes(step);
  const markStepAsCompleted= (step: number) => !isStepCompleted(step) &&
      setState(s => ({ ...s, completedSteps: [...s.completedSteps, step] }));

  /* submission */
  const submitOnboarding = async () => {
    if (!user) {
      Logger.error('Attempted to submit onboarding without user', new Error('No user found'));
      throw new Error('You must be logged in');
    }

    if (validationCtx) {
      const errors = validationCtx.validateAll();
      if (errors.length) {
        const firstStep = Math.min(...errors.map(e => {
          const s = e.scope != null ? Number(e.scope) : 0;
          return isNaN(s) ? 0 : s;
        }));
        Logger.warn('Validation errors during onboarding submission', {
          component: 'OnboardingWizard',
          userId: user.id,
          errors
        });
        goToStep(firstStep);
        throw new Error(errors.map(e => e.message).join('\n'));
      }
    }

    const stateToSave = sanitizeUserConfig(userConfigState);
    await submitConfig(stateToSave);
  };

  return (
    <OnboardingWizardContext.Provider value={{
      ...state,
      goToNextStep,
      goToPreviousStep,
      goToStep,
      isStepCompleted,
      markStepAsCompleted,
      submitOnboarding,
      isSubmitting // Added isSubmitting to the provider value
    }}>
      {children}
    </OnboardingWizardContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useOnboardingWizard = () => {
  const ctx = useContext(OnboardingWizardContext);
  if (!ctx) throw new Error('useOnboardingWizard must be used inside OnboardingWizardProvider');
  return ctx;
};
